import React from 'react';
import parse from 'html-react-parser';
import { Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { ManualMetricItem } from '../../../_types/policy';
import {
  CheckCircleOutlineOutlined,
  WarningAmberOutlined,
  NorthEastOutlined
} from '@mui/icons-material';
import Carousel from 'react-material-ui-carousel';
import { error, neutral, primary, success, warning } from '_lex/_foundations/phxColors';
import datetimeHelper from 'helpers/datetimeHelper';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

type ManualItemProps = {
  metric: ManualMetricItem;
};

export default function ManualItem({ metric }: ManualItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const metricCounts: Array<{ key: 'pending' | 'draft' | 'approved'; value: number; }> = [
    { key: 'pending', value: metric.pendingCount },
    { key: 'draft', value: metric.draftCount },
    { key: 'approved', value: metric.approvedCount },
  ];

  const manualDetailUrl = metric.id ? `/admin/policy/draft-manuals/${metric.id}` : undefined;
  const publishApprovedUrl = metric.id
    ? `/admin/policy/draft-manuals/${metric.id}/publish-approved?returnUrl=${encodeURIComponent('/admin/dashboard')}`
    : undefined;
  const isPublishDisabled = metric.approvedCount === 0;
  const formattedLastPublishedAt = metric.lastPublishedAt
    ? datetimeHelper.formatDate(metric.lastPublishedAt)
    : '';

  function parseContextUri(contextUri: string) {
    const parts = contextUri.split('/');

    if (parts.length >= 5 && parts[2] === 'manuals' && parts[4] === 'content') {
      return {
        manual: parts[3],
        policyId: parts[5],
      };
    }

    return null;
  }

  const handleReviewUpdate = (contextUri: string) => {
    const result = parseContextUri(contextUri);
    if (result) {
      navigate(`/admin/policy/draft-manuals/${result.manual}/policy:${result.policyId}/review-updates`, { state: { from: location.pathname, policyId: result.policyId } });
    } else {
      enqueueSnackbar(t('dashboard.policy.manualMetrics.manualMetricItem.invalidPolicyLink'), { variant: 'error' });
    }
  };

  const publishButton = (
    <Button
      color='secondary'
      size='small'
      component={!isPublishDisabled && publishApprovedUrl ? RouterLink : 'button'}
      to={!isPublishDisabled && publishApprovedUrl ? publishApprovedUrl : undefined}
      disabled={isPublishDisabled}
    >
      {t('dashboard.policy.manualMetrics.manualMetricItem.publishApprovedPolicies')}
    </Button>
  );

  return (
    <Box
      className='manual-metrics-item'
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 2px 460px',
        gap: 4,
        alignItems: 'start',
      }}
    >
      <Stack spacing={3}>
        <Stack>
          {manualDetailUrl ? (
            <Link
              component={RouterLink}
              to={manualDetailUrl}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
                color: primary[700],
                fontWeight: 500,
                fontSize: 16,
                lineHeight: '24px',
                textDecoration: 'underline',
              }}
            >
              {metric.name}
              <NorthEastOutlined sx={{ fontSize: 16 }} />
            </Link>
          ) : (
            <Typography
              variant='subtitle1'
              sx={{ lineHeight: '24px' }}
            >
              {metric.name}
            </Typography>
          )}
          {formattedLastPublishedAt && (
            <Typography
              variant='caption'
              sx={{ color: neutral[500] }}
            >
              {t('dashboard.policy.manualMetrics.manualMetricItem.lastPublishedAt')}:{' '}
              {formattedLastPublishedAt}
            </Typography>
          )}
        </Stack>
        <Stack
          direction='row'
          spacing={6}
        >
          {metricCounts.map(({ key, value }) => (
            <Stack
              key={`metric_${key}`}
              direction='row'
              spacing={2}
              alignItems='baseline'
            >
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: primary[900],
                }}
              >
                {value}
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: neutral[700] }}
              >
                {t(`dashboard.policy.manualMetrics.manualMetricItem.${key}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Box>
          {isPublishDisabled ? (
            <Tooltip title={t('dashboard.policy.manualMetrics.manualMetricItem.noApprovedPoliciesToPublish')}>
              <span>{publishButton}</span>
            </Tooltip>
          ) : (
            publishButton
          )}
        </Box>
      </Stack>
      <Box sx={{ color: neutral[200] }}></Box>
      <Box sx={{ minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
        {metric.policyUpdates.length === 0 ? (
          <Box
            sx={{
              height: 64,
              width: 320,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              px: 3,
              py: 2,
              border: `solid 1px ${neutral[300]}`,
              borderRadius: 2,
              backgroundColor: success[25],
            }}
          >
            <Stack
              direction='row'
              spacing={2}
            >
              <CheckCircleOutlineOutlined sx={{ color: success[700] }} />
              <Typography>{parse(t(`dashboard.policy.manualMetrics.manualMetricItem.noUpdates`))}</Typography>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Carousel
              className='policy-updates'
              autoPlay={false}
              animation='slide'
              navButtonsAlwaysVisible
              navButtonsProps={{
                style: {
                  backgroundColor: 'transparent',
                  color: neutral[400],
                },
              }}
              sx={{
                '&>div:first-of-type>div>div>div>div': {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                },
              }}
            >
              {metric.policyUpdates.slice(0, 5).map((policyUpdate, idx) => {
                const formattedUpdatedAt = policyUpdate.updatedAt
                  ? datetimeHelper.formatDate(policyUpdate.updatedAt)
                  : '';
                return (
                <Box
                  className='policy-update-item'
                  key={policyUpdate.id || idx}
                  sx={{
                    height: 110,
                    width: 320,
                    px: 3,
                    py: 2,
                    border: `solid 1px ${warning[500]}`,
                    borderRadius: 2,
                    backgroundColor: neutral[25],
                  }}
                >
                  <Stack spacing={2}>
                    <Stack>
                      <Stack
                        direction='row'
                        spacing={2}
                      >
                        <WarningAmberOutlined sx={{ color: error[700] }} />
                        <Typography
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {parse(policyUpdate?.title || 'Untitled update')}
                        </Typography>
                      </Stack>
                      {formattedUpdatedAt && (
                        <Stack
                          direction='row'
                          spacing={2}
                        >
                          <Typography
                            variant='caption'
                            sx={{ color: neutral[500] }}
                          >
                            {t('dashboard.policy.manualMetrics.manualMetricItem.updatedAt')}:{' '}
                            {formattedUpdatedAt}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Button
                        className='xs'
                        color='secondary'
                        size='small'
                        onClick={() => handleReviewUpdate(policyUpdate.contentUri)}
                      >
                        {t('dashboard.policy.manualMetrics.manualMetricItem.reviewUpdate')}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                );
              })}
            </Carousel>
          </Box>
        )}
      </Box>
    </Box>
  );
}
